import { useRef, useCallback } from 'react';

/**
 * useZoomAnimation Hook
 * 
 * Provides smooth animated zoom functionality with two modes:
 * 1. Lerp-based chase animation for wheel zoom (accumulates targets, no stutter)
 * 2. Duration-based animation for button zoom (fixed time, ease-out)
 * 
 * Pan is constrained DURING zoom animation to ensure the outermost node
 * always remains visible at the edge. This prevents zooming into empty space
 * when at the boundary - the view automatically adjusts to keep content visible.
 */
export function useZoomAnimation({
  containerRef,
  zoomRef,
  panRef,
  constrainPan,
  setZoomState,
  // zoomHook removed - was causing redundant state updates
  panHook,
  getMinZoom,
}) {
  // Animation refs
  const zoomAnimationRef = useRef(null);
  const animatingZoomRef = useRef(false);
  const targetZoomRef = useRef(1);
  const targetPointRef = useRef({ x: 0, y: 0 });
  
  // Pan correction animation ref (for smooth snap-back after zoom)
  const panCorrectionAnimRef = useRef(null);
  // Delay timeout ref for the pause before correction starts
  const panCorrectionDelayRef = useRef(null);
  
  // Container rect ref (passed from parent or use fallback)
  const containerRectRef = useRef({ width: 0, height: 0, left: 0, top: 0 });

  /**
   * Cancel any pending pan correction (animation or delay)
   * Called when starting a new zoom or when user starts dragging
   */
  const cancelPanCorrection = useCallback(() => {
    if (panCorrectionAnimRef.current) {
      cancelAnimationFrame(panCorrectionAnimRef.current);
      panCorrectionAnimRef.current = null;
    }
    if (panCorrectionDelayRef.current) {
      clearTimeout(panCorrectionDelayRef.current);
      panCorrectionDelayRef.current = null;
    }
  }, []);

  /**
   * Smoothly animate pan back to constrained position after zoom completes
   * Includes a delay before starting the correction for a more natural feel
   * 
   * PERFORMANCE: Only updates React state at the END of animation, not during.
   * During animation, only refs and DOM are updated for smooth 60fps.
   */
  const animatePanCorrection = useCallback((currentPan, finalZoom) => {
    // Cancel any existing correction animation or delay
    cancelPanCorrection();
    
    const constrainedPan = constrainPan(currentPan, finalZoom);
    
    // Check if we need to animate (pan is out of bounds)
    const dx = constrainedPan.x - currentPan.x;
    const dy = constrainedPan.y - currentPan.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    // If close enough, just snap (update React state once)
    if (distance < 1) {
      panHook.setPan(constrainedPan);
      return;
    }
    
    // Delay before starting the correction animation
    const CORRECTION_DELAY = 200; // ms - allows user to settle before correction
    
    panCorrectionDelayRef.current = setTimeout(() => {
      panCorrectionDelayRef.current = null;
      
      // Re-read current pan AND zoom in case user zoomed again during delay
      const startPan = { ...panRef.current };
      const currentZoom = zoomRef.current;
      const recalculatedConstrained = constrainPan(startPan, currentZoom);
      
      // Re-check if still out of bounds
      const newDx = recalculatedConstrained.x - startPan.x;
      const newDy = recalculatedConstrained.y - startPan.y;
      const newDistance = Math.sqrt(newDx * newDx + newDy * newDy);
      
      if (newDistance < 1) {
        panHook.setPan(recalculatedConstrained);
        return;
      }
      
      // Animate smoothly to constrained position
      const startTime = performance.now();
      const duration = Math.min(180, Math.max(80, newDistance * 0.6)); // 80-180ms based on distance
      
      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(1, elapsed / duration);
        
        // Ease out cubic for smooth deceleration
        const eased = 1 - Math.pow(1 - progress, 3);
        
        // Use current zoom (might have changed during animation)
        const animZoom = zoomRef.current;
        
        const animatedPan = {
          x: startPan.x + (recalculatedConstrained.x - startPan.x) * eased,
          y: startPan.y + (recalculatedConstrained.y - startPan.y) * eased,
        };
        
        // PERFORMANCE: Only update ref during animation, NOT React state
        panRef.current = animatedPan;
        // Also update panHook's panRef so pan drag starts from correct position
        if (panHook.panRef) {
          panHook.panRef.current = animatedPan;
        }
        
        // Update DOM directly for smooth 60fps animation
        const canvas = containerRef.current?.querySelector('.flowchart__canvas');
        if (canvas) {
          canvas.style.transform = `translate(${animatedPan.x}px, ${animatedPan.y}px) scale(${animZoom})`;
          // Update CSS variable for zoom-independent elements
          canvas.style.setProperty('--inverse-zoom', 1 / animZoom);
          canvas.dispatchEvent(new CustomEvent('panupdate', { detail: { x: animatedPan.x, y: animatedPan.y, zoom: animZoom } }));
        }
        
        if (progress < 1) {
          panCorrectionAnimRef.current = requestAnimationFrame(animate);
        } else {
          panCorrectionAnimRef.current = null;
          // PERFORMANCE: Only update React state at the END of animation
          // Skip DOM update since we already updated it during animation
          panHook.setPan(animatedPan, true);
        }
      };
      
      panCorrectionAnimRef.current = requestAnimationFrame(animate);
    }, CORRECTION_DELAY);
  }, [constrainPan, panHook, panRef, zoomRef, containerRef, cancelPanCorrection]);

  /**
   * Continuous zoom animation that chases the target using lerp
   * Used for wheel zoom - allows accumulating targets without stutter
   * 
   * When zooming in, the pan is constrained each frame to stay within
   * the hard boundary (outermost node must remain visible at edge).
   */
  const startZoomAnimation = useCallback(() => {
    animatingZoomRef.current = true;
    
    // Cancel any pan correction that might be running
    cancelPanCorrection();
    
    const animate = () => {
      const currentZoom = zoomRef.current;
      const targetZoom = targetZoomRef.current;
      const point = targetPointRef.current;
      
      // Lerp factor - higher = faster (0.25 = smooth, 0.4 = snappy)
      const lerpFactor = 0.35;
      
      // Calculate new zoom with lerp
      const newZoom = currentZoom + (targetZoom - currentZoom) * lerpFactor;
      
      // Check if we're close enough to stop
      const zoomDiff = Math.abs(newZoom - targetZoom);
      const isComplete = zoomDiff < 0.001;
      
      // Use target zoom if complete, otherwise use lerped value
      const finalZoom = isComplete ? targetZoom : newZoom;
      
      // Zoom towards the point
      const zoomRatio = finalZoom / currentZoom;
      const currentPan = panRef.current;
      const newPanX = point.x - (point.x - currentPan.x) * zoomRatio;
      const newPanY = point.y - (point.y - currentPan.y) * zoomRatio;
      
      // Apply hard constraint: pan must stay within bounds for this zoom level
      // This ensures you can never zoom into empty space - the view will
      // automatically adjust to keep the outermost node visible at the edge
      const newPan = constrainPan({ x: newPanX, y: newPanY }, finalZoom);
      
      // Update refs immediately for smooth visual
      zoomRef.current = finalZoom;
      panRef.current = newPan;
      // Also update panHook's refs so setPan/pan drag uses correct values
      if (panHook.zoomRef) {
        panHook.zoomRef.current = finalZoom;
      }
      if (panHook.panRef) {
        panHook.panRef.current = newPan;
      }
      
      // Update DOM directly for smooth animation
      const canvas = containerRef.current?.querySelector('.flowchart__canvas');
      if (canvas) {
        canvas.style.transform = `translate(${newPan.x}px, ${newPan.y}px) scale(${finalZoom})`;
        // Update CSS variable for zoom-independent elements
        canvas.style.setProperty('--inverse-zoom', 1 / finalZoom);
        canvas.dispatchEvent(new CustomEvent('panupdate', { detail: { x: newPan.x, y: newPan.y, zoom: finalZoom } }));
      }
      
      if (!isComplete) {
        zoomAnimationRef.current = requestAnimationFrame(animate);
      } else {
        // Final state update to React (skip DOM update, already done during animation)
        panHook.setPan(newPan, true);
        setZoomState(targetZoom);
        animatingZoomRef.current = false;
        zoomAnimationRef.current = null;
      }
    };
    
    zoomAnimationRef.current = requestAnimationFrame(animate);
  }, [containerRef, zoomRef, panRef, setZoomState, panHook, constrainPan, cancelPanCorrection]);

  /**
   * Queue a zoom target for the lerp animation
   * If already animating, updates target; otherwise starts animation
   */
  const queueZoomToPoint = useCallback((targetZoom, pointX, pointY) => {
    // Update target and point
    targetZoomRef.current = targetZoom;
    targetPointRef.current = { x: pointX, y: pointY };
    
    // Start animation if not already running
    if (!animatingZoomRef.current) {
      startZoomAnimation();
    }
  }, [startZoomAnimation]);

  /**
   * Duration-based zoom animation for button clicks
   * Zooms towards center of viewport with ease-out cubic
   * 
   * Pan is constrained each frame to stay within the hard boundary
   * (outermost node must remain visible at edge).
   * 
   * PERFORMANCE: Only updates refs and DOM during animation.
   * React state is updated only at the END for smooth 60fps.
   */
  const animateZoomTo = useCallback((targetZoom, duration = 150) => {
    // Cancel any existing animation
    if (zoomAnimationRef.current) {
      cancelAnimationFrame(zoomAnimationRef.current);
    }
    
    // Cancel any pan correction that might be running
    cancelPanCorrection();
    
    const startZoom = zoomRef.current;
    const startTime = performance.now();
    animatingZoomRef.current = true;
    
    // Get center of viewport for zoom-towards-center
    const rect = containerRectRef.current;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const startPan = panRef.current;
    
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Ease out cubic for smooth deceleration
      const eased = 1 - Math.pow(1 - progress, 3);
      
      const currentZoom = startZoom + (targetZoom - startZoom) * eased;
      
      // Zoom towards center of viewport
      const zoomRatio = currentZoom / startZoom;
      const newPanX = centerX - (centerX - startPan.x) * zoomRatio;
      const newPanY = centerY - (centerY - startPan.y) * zoomRatio;
      
      // Apply hard constraint: pan must stay within bounds for this zoom level
      const newPan = constrainPan({ x: newPanX, y: newPanY }, currentZoom);
      
      // PERFORMANCE: Only update refs during animation, NOT React state
      zoomRef.current = currentZoom;
      panRef.current = newPan;
      // Also update panHook's refs so setPan/pan drag uses correct values
      if (panHook.zoomRef) {
        panHook.zoomRef.current = currentZoom;
      }
      if (panHook.panRef) {
        panHook.panRef.current = newPan;
      }
      
      // Update DOM directly for smooth 60fps animation
      const canvas = containerRef.current?.querySelector('.flowchart__canvas');
      if (canvas) {
        canvas.style.transform = `translate(${newPan.x}px, ${newPan.y}px) scale(${currentZoom})`;
        // Update CSS variable for zoom-independent elements (edge insert button, etc.)
        canvas.style.setProperty('--inverse-zoom', 1 / currentZoom);
        canvas.dispatchEvent(new CustomEvent('panupdate', { detail: { x: newPan.x, y: newPan.y, zoom: currentZoom } }));
      }
      
      if (progress < 1) {
        zoomAnimationRef.current = requestAnimationFrame(animate);
      } else {
        // PERFORMANCE: Only update React state at the END of animation
        // Skip DOM update since we already updated it during animation
        panHook.setPan(newPan, true);
        setZoomState(targetZoom);
        animatingZoomRef.current = false;
        zoomAnimationRef.current = null;
      }
    };
    
    zoomAnimationRef.current = requestAnimationFrame(animate);
  }, [zoomRef, panRef, setZoomState, containerRef, panHook, constrainPan, cancelPanCorrection]);

  /**
   * Zoom in button handler with animation
   */
  const handleZoomIn = useCallback(() => {
    const currentZoom = zoomRef.current;
    const targetZoom = Math.min(currentZoom * 1.2, 2);
    animateZoomTo(targetZoom);
  }, [zoomRef, animateZoomTo]);

  /**
   * Zoom out button handler with animation
   */
  const handleZoomOut = useCallback(() => {
    const minZoom = getMinZoom();
    const currentZoom = zoomRef.current;
    const targetZoom = Math.max(currentZoom * 0.8, minZoom);
    animateZoomTo(targetZoom);
  }, [zoomRef, getMinZoom, animateZoomTo]);

  return {
    // Refs for external access
    animatingZoomRef,
    targetZoomRef,
    containerRectRef,
    
    // Animation functions
    queueZoomToPoint,
    animateZoomTo,
    cancelPanCorrection, // Call this when starting a drag to prevent conflicts
    
    // Button handlers
    handleZoomIn,
    handleZoomOut,
  };
}

export default useZoomAnimation;

